/** India GST maths: split into CGST+SGST (intra-state), IGST (inter-state) or CGST+UTGST (union territory), amount in words. */
export type GstSupply = "intra" | "inter" | "ut";
const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export interface GstResult { base: number; gst: number; total: number; cgst: number; sgst: number; igst: number; utgst: number }

export function gstCalc(amount: number, ratePct: number, inclusive: boolean, supply: GstSupply): GstResult {
  if (inclusive) {
    // Work in whole paise. GST is always total - base, so base + GST equals the entered total exactly.
    const totalP = Math.round((amount + Number.EPSILON) * 100);
    const exactGstP = totalP - totalP / (1 + ratePct / 100);
    let gstP = totalP - Math.round(totalP / (1 + ratePct / 100));
    // CGST and SGST/UTGST must be equal halves, so an odd-paisa GST moves by 1 paisa (to whichever even value is nearer the exact GST; base takes the difference).
    if (supply !== "inter" && gstP % 2 !== 0) gstP = Math.abs(gstP + 1 - exactGstP) <= Math.abs(gstP - 1 - exactGstP) ? gstP + 1 : gstP - 1;
    const base = (totalP - gstP) / 100, gst = gstP / 100, total = totalP / 100;
    if (supply === "inter") return { base, gst, total, cgst: 0, sgst: 0, utgst: 0, igst: gst };
    const half = gstP / 2 / 100;
    return { base, gst, total, cgst: half, sgst: supply === "intra" ? half : 0, utgst: supply === "ut" ? half : 0, igst: 0 };
  }
  const base = r2(amount);
  if (supply === "inter") { const gst = r2((base * ratePct) / 100); return { base, gst, total: r2(base + gst), cgst: 0, sgst: 0, utgst: 0, igst: gst }; }
  // Each half is charged at half the rate and rounded on its own, so CGST and SGST/UTGST are always equal (as on a tax invoice).
  const half = r2((base * ratePct) / 200);
  const gst = r2(half * 2);
  return { base, gst, total: r2(base + gst), cgst: half, sgst: supply === "intra" ? half : 0, utgst: supply === "ut" ? half : 0, igst: 0 };
}

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
function below1000(n: number): string {
  const h = Math.floor(n / 100), r = n % 100;
  const parts: string[] = [];
  if (h) parts.push(`${ONES[h]} Hundred`);
  if (r) parts.push(r < 20 ? ONES[r] : `${TENS[Math.floor(r / 10)]}${r % 10 ? " " + ONES[r % 10] : ""}`);
  return parts.join(" ");
}
/** Amount in Indian words (lakh/crore), e.g. 1,18,000.50 -> "Rupees One Lakh Eighteen Thousand and Fifty Paise Only". */
export function rupeesInWords(amount: number): string {
  const a = Math.abs(r2(amount));
  let rupees = Math.floor(a);
  const paise = Math.round((a - rupees) * 100);
  if (rupees === 0 && paise === 0) return "Rupees Zero Only";
  const units: [number, string][] = [[10000000, "Crore"], [100000, "Lakh"], [1000, "Thousand"]];
  const parts: string[] = [];
  for (const [v, name] of units) {
    const q = Math.floor(rupees / v);
    if (q) { parts.push(`${rupees >= 1e12 ? q : below1000(q)} ${name}`.replace(/^(\d+) /, "$1 ")); rupees -= q * v; }
  }
  if (rupees) parts.push(below1000(rupees));
  let out = rupees === 0 && parts.length === 0 ? "" : parts.join(" ");
  out = out ? `Rupees ${out}` : "";
  if (paise) out += `${out ? " and " : "Paise "}${below1000(paise)}${out ? " Paise" : ""}`;
  return `${out} Only`.replace("  ", " ");
}
